import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';

export const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    const result = await login(email, password);
    setLoading(false);
    if (result.success) navigate('/');
    else setError(result.error);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-paper py-16 px-6">
      <div className="w-full max-w-md">
        <div className="text-center mb-9">
          <Link to="/" className="inline-flex items-baseline gap-2 mb-6">
            <span className="font-display font-bold text-[19px] tracking-[-0.03em]">Cape&nbsp;Town</span>
            <span className="font-display-italic text-[21px] leading-none">Monthly</span>
          </Link>
          <h1 className="font-display leading-[1.06] tracking-[-0.03em] text-ink" style={{ fontSize: 'clamp(32px, 4vw, 44px)' }} data-testid="login-title">
            Welcome <span className="font-display-italic">back</span>
          </h1>
          <p className="mt-2 text-[15px] text-muted">Sign in to your account</p>
        </div>

        <div className="bg-white border rounded-[12px] p-8 shadow-[0_1px_2px_rgba(0,0,0,0.03)]" style={{ borderColor: 'var(--line-2)' }}>
          <form onSubmit={handleSubmit} className="space-y-5">
            {error && (
              <Alert variant="destructive" data-testid="login-error-alert">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <div className="space-y-2">
              <Label htmlFor="email" className="text-[13px] font-semibold text-ink-2">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
                data-testid="login-email-input"
                className="h-11 rounded-md border bg-white focus-visible:ring-2 focus-visible:ring-[color:var(--sea)] focus-visible:ring-offset-0"
                style={{ borderColor: 'var(--line-2)' }}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-[13px] font-semibold text-ink-2">Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                data-testid="login-password-input"
                className="h-11 rounded-md border bg-white focus-visible:ring-2 focus-visible:ring-[color:var(--sea)] focus-visible:ring-offset-0"
                style={{ borderColor: 'var(--line-2)' }}
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="btn btn-sea btn-lg w-full disabled:opacity-60"
              data-testid="login-submit-btn"
            >
              {loading ? 'Signing in…' : 'Sign in'}
            </button>
          </form>

          <div className="mt-6 text-center space-y-2">
            <Link to="/forgot-password" className="text-[14px] text-sea hover:underline" data-testid="login-forgot-password-link">
              Forgot password?
            </Link>
            <p className="text-[14px] text-muted">
              No account?{' '}
              <Link to="/register" className="text-ink font-semibold hover:text-sea transition-colors" data-testid="login-register-link">
                Create one
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
